import {Map} from 'immutable';
import {
  type BindingPathMap,
  type ShallowBindingPath,
  isDeepBindingPath,
  isShallowBindingPath,
} from '@packages/systems/dynamo/binding-types';
import {getTokenPathsMatchingPathPattern} from '@packages/systems/core/utils/EmbedUtils/all';
import {
  Plugins,
  Expressions,
  matchExpression,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {type Json, matchJson, getString, get} from '@packages/utilities/json';
import {namesEqual, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {constant} from '@packages/utilities/fp/utils';

export type BindingDataPaths = {
  [key: string]: string[];
};

export const __DEPRECATED__getBindingDataPaths = (
  bindings: BindingPathMap
): BindingDataPaths => {
  return bindings.reduce<Record<string, string[]>>(
    (dataPaths, bindingPath, bindingKey): BindingDataPaths => {
      if (isDeepBindingPath(bindingPath)) {
        const deepBindingDataPaths =
          __DEPRECATED__getBindingDataPaths(bindingPath);
        recordDeepDataPaths(dataPaths, deepBindingDataPaths, bindingKey);
      } else if (isShallowBindingPath(bindingPath)) {
        dataPaths[bindingKey] = __DEPRECATED__getDataPath(bindingPath);
      }
      return dataPaths;
    },
    {}
  );
};

const __DEPRECATED__getDataPath = (
  bindingPath: ShallowBindingPath
): string[] => {
  return bindingPath
    .takeUntil(__DEPRECATED__isMetadataBindingPart)
    .toArray()
    .map((part) => part.get('slug', ''));
};

const __DEPRECATED__isMetadataBindingPart = (
  part: Map<string, string>
): boolean => {
  return part.get('id') === part.get('slug') && part.get('slug') !== 'slug';
};

export function getElementBindingDataPaths(
  element: ExpressionElement
): BindingDataPaths {
  const elementData = Expressions.getElementData(element);
  const bindingDataPaths = getBindingDataPaths(elementData);

  if (!bindingDataPaths || Array.isArray(bindingDataPaths)) {
    return {};
  }

  return bindingDataPaths;
}

const dynBindPath: ExpressionPath = [
  {in: 'Record', at: 'dyn'},
  {in: 'Record', at: 'bind'},
];

const commerceMultiTextEditType = qualify(
  Plugins.name('Commerce'),
  typeName('MultiTextEdit')
);

const getBindingDataPaths = matchExpression({
  Record: (_rec, elementDataExpr) => {
    const dynBindExpr = Expressions.getIn(elementDataExpr, dynBindPath);
    return dynBindExpr ? parseBindExpr(dynBindExpr) : null;
  },
  Literal: ({name, value: elementDataJson}) => {
    // TODO: Commerce/MultiTextEdit needs to be converted to an Alias
    // https://webflow.atlassian.net/browse/PLAT-639
    if (namesEqual(name, commerceMultiTextEditType)) {
      const dynBindLiteral = get(elementDataJson, ['dyn', 'bind']);
      return dynBindLiteral
        ? literal_getBindingDataPaths(dynBindLiteral)
        : null;
    }
    return null;
  },
  _: (_) => null,
});

const getDataPath = (path: Expression[]): string[] | null => {
  const bindingPath = path.map(getPathPart);
  const dataPath: Array<string> = [];

  for (let i = 0; i < bindingPath.length; i++) {
    const part = bindingPath[i];

    // @ts-expect-error - TS2345 - Argument of type '{ [key: string]: string; } | undefined' is not assignable to parameter of type '{ [key: string]: string; }'.
    if (isMetadataBindingPart(part)) {
      break;
    }

    // @ts-expect-error - TS18048 - 'part' is possibly 'undefined'.
    dataPath.push(part.slug || '');
  }

  return dataPath;
};

const getPathPart = matchExpression<{
  [key: string]: string;
}>({
  Record: (part) =>
    Object.keys(part).reduce<Record<string, any>>((memo, key) => {
      // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'ExpressionF<Expression>'.
      const val = Expressions.getText(part[key]);
      if (val !== null) {
        memo[key] = val;
      }
      return memo;
    }, {}),
  _: (_) => ({}),
});

type ParsedBindExpr = BindingDataPaths | string[] | null;
const parseBindExpr = matchExpression<ParsedBindExpr>({
  Record: (bindings) => {
    return Object.keys(bindings).reduce<Record<string, any>>(
      (dataPaths, bindingKey) => {
        // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'ExpressionF<Expression>'.
        const dataPathValue = parseBindExpr(bindings[bindingKey]);

        if (!dataPathValue) {
          return dataPaths;
        }

        if (Array.isArray(dataPathValue)) {
          dataPaths[bindingKey] = dataPathValue;
        } else {
          recordDeepDataPaths(dataPaths, dataPathValue, bindingKey);
        }

        return dataPaths;
      },
      {}
    );
  },
  List: getDataPath,
  _: (_) => null,
});

function literal_getBindingDataPaths(bind: Json) {
  const bindingDataPaths = parseBindLiteral(bind);

  if (!bindingDataPaths || Array.isArray(bindingDataPaths)) {
    return {};
  }

  return bindingDataPaths;
}

const constantNull = constant(null);

const literal_getDataPath = (path: Json[]): string[] | null => {
  const bindingPath = path.map(literal_getPathPart);
  const dataPath: Array<string> = [];

  for (let i = 0; i < bindingPath.length; i++) {
    const part = bindingPath[i];

    if (!part || isMetadataBindingPart(part)) {
      break;
    }

    dataPath.push(part.slug || '');
  }

  return dataPath;
};

const literal_getPathPart = matchJson({
  Null: constantNull,
  Boolean: constantNull,
  Number: constantNull,
  String: constantNull,
  Array: constantNull,
  Object: (part) => {
    return Object.keys(part).reduce<Record<string, any>>((memo, key) => {
      // @ts-expect-error - TS2345 - Argument of type 'Json | undefined' is not assignable to parameter of type 'Json'.
      const val = getString(part[key]);
      if (val !== null) {
        memo[key] = val;
      }
      return memo;
    }, {});
  },
});

const parseBindLiteral = matchJson({
  Null: constantNull,
  Boolean: constantNull,
  Number: constantNull,
  String: constantNull,
  Array: literal_getDataPath,
  Object: (bindings) => {
    return Object.keys(bindings).reduce<Record<string, any>>(
      (dataPaths, bindingKey) => {
        // @ts-expect-error - TS2345 - Argument of type 'Json | undefined' is not assignable to parameter of type 'Json'.
        const dataPathValue = parseBindLiteral(bindings[bindingKey]);

        if (!dataPathValue) {
          return dataPaths;
        }

        if (Array.isArray(dataPathValue)) {
          dataPaths[bindingKey] = dataPathValue;
        } else {
          recordDeepDataPaths(dataPaths, dataPathValue, bindingKey);
        }

        return dataPaths;
      },
      {}
    );
  },
});

// Appends data paths from deepDataPaths to dataPaths, mutating the latter.
// Uses an expanded bindingKey to record deep data paths.
function recordDeepDataPaths(
  dataPaths: BindingDataPaths,
  deepDataPaths: BindingDataPaths,
  bindingKey: string
) {
  for (const deepBindingKey in deepDataPaths) {
    // @ts-expect-error - TS2322 - Type 'string[] | undefined' is not assignable to type 'string[]'.
    dataPaths[`${bindingKey}.${deepBindingKey}`] =
      deepDataPaths[deepBindingKey];
  }
}

function isMetadataBindingPart(part: {[key: string]: string}): boolean {
  return part.id === part.slug && part.slug !== 'slug';
}

export const getDynTokenBindingPaths = (
  replaceable: string | number | null,
  nodeType: string
): Array<Array<string>> => {
  switch (nodeType) {
    case 'HtmlEmbed':
    case 'Title':
    case 'Meta':
    case 'CustomCode': {
      if (replaceable && typeof replaceable === 'string') {
        return getTokenPathsMatchingPathPattern(replaceable);
      }
    }
  }
  return [];
};
